import React, { useState } from 'react';
import { useLanguage } from './LanguageContext';
import { apiPost, apiPut } from '../utils/api';

const UserForm = ({ user, branches, onClose, onSave }) => {
  const { print_lang } = useLanguage();
  const isEdit = !!user;

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'officer',
    branch_id: user?.branch_id || '',
    status: user?.status || 'active',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.email || !formData.branch_id) {
      setError('Name, email, and branch are required');
      return;
    }

    if (!isEdit && !formData.password) {
      setError('Password is required for new users');
      return;
    }

    if (!isEdit && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      let result;

      if (isEdit) {
        // Update user (don't send password if empty)
        const updateData = {
          name: formData.name,
          role: formData.role,
          branch_id: formData.branch_id,
          status: formData.status,
        };
        result = await apiPut(`/api/admin/users/${user.user_id}`, updateData);
      } else {
        // Create user
        result = await apiPost('/api/admin/users', formData);
      }

      if (result.success) {
        onSave();
      } else {
        setError(result.message || 'Save failed');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isEdit ? print_lang('user.editUser', 'Edit User') : print_lang('user.addUser', 'Add User')}
          </h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}

            <div className="form-group">
              <label className="form-label">{print_lang('user.name', 'Name')} *</label>
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">{print_lang('user.email', 'Email')} *</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isEdit}
              />
            </div>

            {!isEdit && (
              <div className="form-group">
                <label className="form-label">{print_lang('user.password', 'Password')} *</label>
                <input
                  type="password"
                  name="password"
                  className="form-input"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 6 characters"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{print_lang('user.role', 'Role')} *</label>
              <select
                name="role"
                className="form-select"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="admin">{print_lang('user.roleAdmin', 'Admin')}</option>
                <option value="officer">{print_lang('user.roleOfficer', 'Officer')}</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{print_lang('user.branch', 'Branch')} *</label>
              <select
                name="branch_id"
                className="form-select"
                value={formData.branch_id}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Branch --</option>
                {branches.map((branch) => (
                  <option key={branch.branch_id} value={branch.branch_id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            {isEdit && (
              <div className="form-group">
                <label className="form-label">{print_lang('user.status', 'Status')}</label>
                <select
                  name="status"
                  className="form-select"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="active">{print_lang('common.active', 'Active')}</option>
                  <option value="inactive">{print_lang('common.inactive', 'Inactive')}</option>
                </select>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {print_lang('common.cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? print_lang('common.loading', 'Loading...') : print_lang('common.save', 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;
